import { api } from './api';

export interface ReceiptSettings { storeName?: string; address?: string; license?: string; header?: string; footer?: string; policy?: string; showLicense?: boolean; logo?: string | null }

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const usd = (c: number) => `${c < 0 ? '-' : ''}$${(Math.abs(c) / 100).toFixed(2)}`;
const size = (ml: number) => (ml <= 1 ? '' : ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`);
const lines = (s?: string) => (s ?? '').split('\n').map((l) => l.trim()).filter(Boolean).map((l) => `<div class="c">${esc(l)}</div>`).join('');

/** A receipt sized for an 80 mm thermal printer (it also prints fine on letter paper). */
export function receiptHtml(sale: any, s: ReceiptSettings, extra: { cashier?: string; customer?: string } = {}): string {
  const rows = (sale.lines as any[]).map((l) => `
    <div class="r"><span>${l.qty !== 1 ? `${l.qty} × ` : ''}${esc(`${l.brand} ${l.name}`)} ${esc(size(l.size_ml))}</span><span>${usd(l.unit_price_cents * l.qty)}</span></div>
    ${l.discount_cents ? `<div class="r sub"><span>Discount</span><span>-${usd(l.discount_cents)}</span></div>` : ''}`).join('');
  const tenders = (sale.tenders as any[]).map((t) => `<div class="r"><span>${esc(String(t.method).replace('_', ' '))}${t.method === 'gift' && t.reference ? ` ${esc(String(t.reference).slice(-4))}` : ''}</span><span>${usd(t.amount_cents)}</span></div>`).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title><style>
    @page { size: 80mm auto; margin: 4mm }
    * { box-sizing: border-box } body { width: 72mm; margin: 0 auto; font: 12px/1.35 'Courier New', monospace; color: #000 }
    .c { text-align: center } .r { display: flex; justify-content: space-between; gap: 8px } .sub { padding-left: 12px; font-size: 11px }
    hr { border: 0; border-top: 1px dashed #000; margin: 6px 0 } .big { font-size: 15px; font-weight: bold } img { max-width: 60mm; max-height: 24mm; display: block; margin: 0 auto 4px }
  </style></head><body>
    ${s.logo ? `<img src="${s.logo}" alt="">` : ''}
    <div class="c big">${esc(s.storeName ?? '')}</div>
    ${s.address ? `<div class="c">${esc(s.address)}</div>` : ''}
    ${s.showLicense && s.license ? `<div class="c">NJ licence ${esc(s.license)}</div>` : ''}
    ${lines(s.header)}
    <hr>
    <div class="r"><span>${new Date(sale.sold_at).toLocaleString()}</span><span>#${String(sale.id).slice(0, 8)}</span></div>
    ${extra.cashier ? `<div>Cashier: ${esc(extra.cashier)}</div>` : ''}${extra.customer ? `<div>Customer: ${esc(extra.customer)}</div>` : ''}
    <hr>${rows}<hr>
    <div class="r"><span>Subtotal</span><span>${usd(sale.subtotal_cents - sale.discount_cents)}</span></div>
    <div class="r"><span>NJ sales tax</span><span>${usd(sale.tax_cents)}</span></div>
    ${sale.cash_rounding_cents ? `<div class="r"><span>Rounding</span><span>${usd(sale.cash_rounding_cents)}</span></div>` : ''}
    <div class="r big"><span>TOTAL</span><span>${usd(sale.total_cents)}</span></div>
    <hr>${tenders}
    ${sale.points_earned ? `<hr><div class="c">You earned ${sale.points_earned} points</div>` : ''}
    <hr>${lines(s.policy)}${lines(s.footer)}<div class="c">Thank you!</div>
  </body></html>`;
}

/** Fetch a sale and the store's receipt text, then open the print dialog. Returns false if the browser blocked the window. */
export async function printReceipt(saleId: string, cashier?: string, customer?: string): Promise<boolean> {
  const [sale, settings] = await Promise.all([api<any>(`/api/sales/${saleId}`), api<ReceiptSettings>('/api/receipt-settings')]);
  const w = window.open('', '_blank', 'width=380,height=640');
  if (!w) return false;
  w.document.open(); w.document.write(receiptHtml(sale, settings, { cashier, customer })); w.document.close();
  w.focus(); setTimeout(() => w.print(), 250);
  return true;
}
